/**
 * Centralized user-facing copy for toast notifications and inline messages.
 *
 * All strings live here so a future i18n pass (Spanish) can find them in
 * one place. Static strings are plain values; dynamic strings are functions.
 */
import toast from 'react-hot-toast';
import type { AddProjectResult } from '../contexts/ProjectsContext';

export const TOAST = {
    project: {
        created: 'Project created',
        createdWithSeedingWarning:
            'Project created — could not seed from estimate. Add tasks and materials manually.',
        createdWithGeofenceWarning:
            'Project created — geofence not configured. Set the site address in Command Center to enable on-site clock-in.',
        createdWithBothWarnings:
            'Project created — estimate seeding failed and geofence not configured. Set both manually inside the project.',
        createFailed: (msg: string) => `Failed to create project: ${msg}`,
        deleted: 'Project deleted',
        deleteFailed: (msg: string) => `Failed to delete project: ${msg}`,
        statusUpdated: 'Project status updated',
        statusUpdateFailed: (msg: string) => `Failed to update project status: ${msg}`,
    },
    crew: {
        assigned: 'Crew updated',
        assignFailed: (msg: string) => `Failed to update crew: ${msg}`,
    },
    timeClock: {
        clockedIn: 'Clocked in',
        clockedInOffSite: (distanceFt: number) =>
            `Clocked in — ${distanceFt} ft off-site. Admin will see this flagged.`,
        clockedOut: 'Clocked out',
        clockFailed: (msg: string) => `Time clock failed: ${msg}`,
    },
    materials: {
        statusUpdated: 'Material status updated',
        addedToShoppingList: 'Added to shopping list',
        bulkUpdated: (count: number) => `${count} materials updated`,
    },
    tasks: {
        completed: 'Task marked complete',
        autoTagApplied: (count: number) => `${count} tasks re-tagged by phase`,
    },
} as const;

/**
 * Surface the warnings array from `addProject()` as a single user-visible toast.
 * Returns true if a success toast was shown, false if a warning toast was shown.
 */
export function surfaceAddProjectResult(result: AddProjectResult): boolean {
    const hasSeeding = result.warnings.includes('estimate-seeding-failed');
    const hasGeofence = result.warnings.includes('geocoding-failed');

    if (hasSeeding && hasGeofence) {
        toast(TOAST.project.createdWithBothWarnings, { icon: '⚠️', duration: 5000 });
        return false;
    }
    if (hasSeeding) {
        toast(TOAST.project.createdWithSeedingWarning, { icon: '⚠️', duration: 5000 });
        return false;
    }
    if (hasGeofence) {
        toast(TOAST.project.createdWithGeofenceWarning, { icon: '⚠️', duration: 5000 });
        return false;
    }

    toast.success(TOAST.project.created);
    return true;
}
